import React, { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import ScheduleBulkUpdate from '~/components/Offers/Edit/common/ScheduleBulkUpdate';
import { useScheduleSection } from '~/components/Offers/Edit/common/useScheduleSection';

import { NO_STANDALONE_SCHEDULE_BRANDS, brands } from '~/consts/brands';
import { LIST_VISIBILITY, LUX_PLUS_SCHEDULE } from '~/consts/schedule';

import { getScheduleTypeLabel } from './campaignScheduleHistoryDisplay';
import type { BrandSchedule } from './types';

// Manual creation only covers list_visibility/lux_plus — availability/online_purchase are
// offer-wide and already edited via ImportedSalesforceDatesSection.
const MANUALLY_CREATABLE_TYPES: Array<BrandSchedule['type']> = [LIST_VISIBILITY, LUX_PLUS_SCHEDULE];

interface Props {
  offerId: string;
  open: boolean;
  onClose: () => void;
  brandSchedules: Array<BrandSchedule>;
}

export default function AddCampaignScheduleDialog({ offerId, open, onClose, brandSchedules }: Props) {
  const [selectedBrand, setSelectedBrand] = useState<Brand | ''>('');
  const [selectedType, setSelectedType] = useState<BrandSchedule['type'] | ''>('');
  const { onSaveFinished, onError } = useScheduleSection({ offerId, shouldMigrateSchedule: false });

  const reset = () => {
    setSelectedBrand('');
    setSelectedType('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePickerSaveFinished = () => {
    reset();
    return onSaveFinished();
  };

  if (selectedBrand && selectedType) {
    return (
      <ScheduleBulkUpdate
        brand={selectedBrand}
        isOpen={open}
        offerId={offerId}
        type={selectedType}
        currentSchedules={brandSchedules.filter(
          (schedule) => schedule.brand === selectedBrand && schedule.type === selectedType,
        )}
        allSchedules={brandSchedules}
        onClose={handleClose}
        onSaveFinished={handlePickerSaveFinished}
        onError={() => onError(new Error('Failed to save schedule'))}
        title={`Add ${getScheduleTypeLabel(selectedType)} Schedule`}
      />
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Schedule</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Brand"
            select
            value={selectedBrand}
            onChange={(event) => setSelectedBrand(event.target.value as Brand)}
          >
            {brands
              .filter((brand) => !NO_STANDALONE_SCHEDULE_BRANDS.includes(brand.value))
              .map((brand) => (
                <MenuItem key={brand.value} value={brand.value}>
                  {brand.title}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            label="Schedule type"
            select
            value={selectedType}
            onChange={(event) => setSelectedType(event.target.value as BrandSchedule['type'])}
          >
            {MANUALLY_CREATABLE_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {getScheduleTypeLabel(type)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
